import axios, { AxiosInstance } from 'axios';
import { getAuth } from 'firebase/auth';
import { Notification } from '../notification_handler';

type TEnvironment = 'dev' | 'staging' | 'prod';

const APP_ENVIRONMENT: TEnvironment = process.env.REACT_APP_ENVIRONMENT as TEnvironment || 'dev';

const API_BASE_URLS: Record<TEnvironment, string> = {
  dev: 'https://api-dev.vespid.my',
  staging: 'https://api-staging.vespid.my',
  prod: 'https://api.vespid.my',
};

const getAPIBaseURL = () => {
  return API_BASE_URLS[APP_ENVIRONMENT];
};

class VespidFunctions {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: getAPIBaseURL(),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add response interceptor for error handling
    this.instance.interceptors.response.use(
      (response) => {
        // Return successful responses as-is
        return response;
      },
      (error) => {
        // Handle errors centrally
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  // Centralized error handling method
  private handleError(error: any): void {
    // Skip toast for 400 failed-precondition (partner conflict) - handled by UI with confirmation dialog
    if (error.response?.status === 400 && error.response?.data?.error?.code === 'failed-precondition') {
      return;
    }
    
    if (error.response?.data?.error?.message) {
      Notification.error(error.response.data.error.message);
    } else if (error.response?.data?.message) {
      Notification.error(error.response.data.message);
    } else {
      Notification.error('An unexpected error occurred. Please contact your IT or Vespid Tech for support.');
    }
  }

  // Helper method to get the authentication token
  private async getAuthToken(): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;
    return user ? await user.getIdToken() : '';
  }

  // GET request method
  public async get(url: string, params?: any): Promise<any> {
    const token = await this.getAuthToken();
    const response = await this.instance.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params,
    });
    return response.data;
  }

  // POST request method
  public async post(url: string, data: any): Promise<any> {
    const token = await this.getAuthToken();
    const response = await this.instance.post(url, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  // POST request method without authentication (for register/login)
  public async postPublic(url: string, data: any): Promise<any> {
    const response = await this.instance.post(url, data);
    return response.data;
  }
}

// Export an instance of the class
export const vespidFunctionsAPI = new VespidFunctions();